import React, { useCallback, useState } from "react";
import { Modal, Typography, Button } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import moment from "moment";
import { useToast } from "../../../hooks/toast";
import { IRequirement } from "..";
import api from "../../../services/api";

const { Text } = Typography;

interface IActivityModalProps {
  visible: boolean;
  activity?: IRequirement;
  handleCloseModal: (activity_id?: string) => void;
}

interface IActivityFormData {
  identifier: string;
  description: string;
  hours?: number;
  type: string;
  consultant_id?: string;
  project_id: string;
  allocation_month: moment.Moment;
  estimator_id: string;
}

const DeleteActivityModal: React.FC<IActivityModalProps> = ({
  visible,
  activity,
  handleCloseModal,
}) => {
  const { t } = useTranslation(["activities"]);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const handleClose = useCallback(
    (id?: string) => {
      handleCloseModal(id);
    },
    [handleCloseModal],
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Ocorreu um erro ao criar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro em deletar",
        description,
      });
    },
    [addToast],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(old => !old);
    try {
      await api.delete(`/activities/${activity?.id}`);
      addToast({
        type: "success",
        title: "Deletar Atividade",
        description: "A atividade foi deletada com sucesso",
      });

      handleClose(activity?.id || "");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    } finally {
      setLoading(old => !old);
    }
  }, [activity?.id, addToast, handleClose]);

  const dateAllocation = useCallback(() => {
    const date = new Date(activity?.year || 2024, activity?.month || 12);
    return moment(date).format("MMMM [de] YYYY");
  }, [activity?.month, activity?.year]);

  const textDelete = useCallback(() => {
    if (activity?.type === "ALLOCATION_MONTH") {
      return `de alocação de ${dateAllocation()}`;
    }
    return `"${activity?.description}"`;
  }, [activity?.description, activity?.type, dateAllocation]);

  return (
    <Modal
      title={t("actions.options.delete")}
      visible={visible}
      onOk={() => handleSubmit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Text>
        Você tem certeza que deseja deletar a atividade
        <Text strong> {textDelete()} </Text>
      </Text>
    </Modal>
  );
};

export default DeleteActivityModal;
